import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UploadSimple, CheckCircle, Trash, FileArrowUp } from "@phosphor-icons/react";

const DOC_TYPES = [
  { key: "PAN Card", required: true },
  { key: "Aadhaar Front", required: true },
  { key: "Aadhaar Back", required: true },
  { key: "Photo", required: true },
  { key: "Cancelled Cheque", required: true },
  { key: "Address Proof", required: false },
  { key: "MSME", required: false },
  { key: "GST Registration", required: false },
  { key: "Office Photo", required: false },
];

export default function DevelopmentForm() {
  const [form, setForm] = useState({
    submission_date: new Date().toISOString().slice(0, 10),
    name: "", email: "", phone: "", residence_address: "", whatsapp_number: "",
    agent_support_email: "", agent_support_phone: "", domain_name: "", company_name: "",
    company_address: "", support_executive_name: "",
    account_holder: "", bank_name: "", account_number: "", ifsc: "", upi_id: "",
  });
  const [docs, setDocs] = useState({});
  const [uploading, setUploading] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const upload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const r = await api.post("/public/development/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((p) => ({ ...p, [docType]: r.data }));
      toast.success(`${docType} uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(null); }
  };

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) { toast.error("Name, Email and Phone are required"); return; }
    setBusy(true);
    try {
      await api.post("/public/development", { ...form, documents: Object.values(docs) });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  if (done) return (
    <Shell>
      <div className="text-center py-14">
        <div className="h-16 w-16 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-5">
          <CheckCircle size={40} weight="fill" className="text-emerald-500" />
        </div>
        <h1 className="font-heading text-2xl text-zinc-900 mb-2 tracking-tight">Thank you!</h1>
        <p className="text-sm text-zinc-500 max-w-sm mx-auto">Your details have been submitted successfully. Our team will get in touch with you shortly.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl text-zinc-900 tracking-tight">Let's get you set up</h1>
        <p className="text-sm text-zinc-500 mt-1.5">Please fill in your details below. Fields marked <span className="text-red-500">*</span> are required.</p>
      </div>

      <Section title="Your Details">
        <F label="Date"><Input type="date" value={form.submission_date} onChange={(e) => set("submission_date", e.target.value)} className="rounded-sm h-10" data-testid="d-date" /></F>
        <F label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm h-10" data-testid="d-name" /></F>
        <F label="Email *"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-sm h-10" data-testid="d-email" /></F>
        <F label="Phone Number *"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-sm h-10" data-testid="d-phone" /></F>
        <F label="WhatsApp Number"><Input value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} className="rounded-sm h-10" data-testid="d-whatsapp" /></F>
        <F label="Residence Address" span><Textarea value={form.residence_address} onChange={(e) => set("residence_address", e.target.value)} className="rounded-sm" data-testid="d-address" /></F>
      </Section>

      <Section title="Company & Support">
        <F label="Domain Name"><Input value={form.domain_name} onChange={(e) => set("domain_name", e.target.value)} className="rounded-sm h-10" data-testid="d-domain" /></F>
        <F label="Your Company Name"><Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="rounded-sm h-10" data-testid="d-company" /></F>
        <F label="Company Address" span><Textarea value={form.company_address} onChange={(e) => set("company_address", e.target.value)} className="rounded-sm" data-testid="d-companyaddr" /></F>
        <F label="Agent Support Email"><Input type="email" value={form.agent_support_email} onChange={(e) => set("agent_support_email", e.target.value)} className="rounded-sm h-10" data-testid="d-supportemail" /></F>
        <F label="Agent Support Phone"><Input value={form.agent_support_phone} onChange={(e) => set("agent_support_phone", e.target.value)} className="rounded-sm h-10" data-testid="d-supportphone" /></F>
      </Section>

      <Section title="Bank Details for Commission">
        <F label="Account Holder"><Input value={form.account_holder} onChange={(e) => set("account_holder", e.target.value)} className="rounded-sm h-10" data-testid="d-holder" /></F>
        <F label="Bank Name"><Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} className="rounded-sm h-10" data-testid="d-bank" /></F>
        <F label="Account Number"><Input value={form.account_number} onChange={(e) => set("account_number", e.target.value)} className="rounded-sm h-10" data-testid="d-account" /></F>
        <F label="IFSC"><Input value={form.ifsc} onChange={(e) => set("ifsc", e.target.value)} className="rounded-sm h-10" data-testid="d-ifsc" /></F>
        <F label="UPI ID"><Input value={form.upi_id} onChange={(e) => set("upi_id", e.target.value)} className="rounded-sm h-10" data-testid="d-upi" /></F>
      </Section>

      <Section title="Documents">
        <div className="col-span-full grid sm:grid-cols-2 gap-3">
          {DOC_TYPES.map((d) => (
            <label key={d.key} data-testid={`upload-${d.key.replace(/\s/g, "-")}`} className={`flex items-center justify-between gap-3 border rounded-sm px-4 h-14 cursor-pointer transition-colors ${docs[d.key] ? "border-emerald-400 bg-emerald-50" : "border-zinc-200 hover:border-zinc-400"}`}>
              <div className="flex items-center gap-2 min-w-0">
                {docs[d.key] ? <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0" /> : <FileArrowUp size={18} className="text-zinc-400 shrink-0" />}
                <div className="min-w-0"><div className="text-sm text-zinc-700 truncate">{d.key}{d.required && <span className="text-red-500"> *</span>}</div>
                  <div className="text-[11px] text-zinc-400 truncate">{docs[d.key]?.filename || (uploading === d.key ? "Uploading…" : "PDF/JPG/PNG · max 10MB")}</div></div>
              </div>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => upload(d.key, e.target.files?.[0])} />
              <UploadSimple size={16} className="text-zinc-400 shrink-0" />
            </label>
          ))}
        </div>
        <p className="col-span-full text-[11px] text-zinc-400">Only PDF, JPG, PNG or WEBP files up to 10MB are accepted. Optional documents can be left blank.</p>
      </Section>

      <Section title="Support Executive">
        <F label="Your Support Executive Name" span><Input value={form.support_executive_name} onChange={(e) => set("support_executive_name", e.target.value)} className="rounded-sm h-10" data-testid="d-support" /></F>
      </Section>

      <Button data-testid="dev-submit" onClick={submit} disabled={busy} className="w-full h-12 rounded-sm bg-brand hover:bg-brand-dark">{busy ? "Submitting…" : "Submit Form"}</Button>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-white font-body py-8">
    <div className="max-w-2xl mx-auto px-4 sm:px-6">
      <div className="bg-brand-dark px-5 py-5 flex items-center gap-4">
        <div className="bg-white rounded-none px-3 py-2 inline-flex items-center">
          <img src="/paisapaid-logo.webp" alt="PaisaPaid Business ERP" className="h-8 w-auto" />
        </div>
        <div>
          <div className="font-heading text-lg text-white tracking-tight leading-tight">CRM Development Form</div>
          <div className="text-xs text-indigo-200">Powered by PaisaPaid Business ERP</div>
        </div>
      </div>
      <div className="pt-8">{children}</div>
      <p className="text-center text-[11px] text-zinc-400 mt-8">© {new Date().getFullYear()} PaisaPaid · Your information is kept private and secure.</p>
    </div>
  </div>
);
const Section = ({ title, children }) => (
  <div className="mb-8">
    <div className="flex items-center gap-2.5 mb-4">
      <div className="h-4 w-1 rounded-full bg-brand" />
      <div className="text-[11px] uppercase tracking-widest text-brand font-semibold">{title}</div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </div>
);
const F = ({ label, children, span }) => (<div className={span ? "sm:col-span-2" : ""}><Label className="text-xs text-zinc-500 mb-1.5 block">{label}</Label>{children}</div>);
